'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@achra/ui/button';
import { cn } from '@achra/ui/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@achra/ui/tooltip';
import { Check, Copy, type LucideIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import React, { createContext, useContext } from 'react';
import { useCopyButton } from './use-copy-button';
type Value = string | (() => string);
interface CopyButtonContextValue extends ReturnType<typeof useCopyButton> {
  value: Value;
}
const CopyButtonContext = createContext<CopyButtonContextValue | null>(null);
function useCopyButtonContext() {
  const context = useContext(CopyButtonContext);
  if (!context) {
    throw new Error("CopyButton components must be used within a <CopyButton>");
  }
  return context;
}

/**
 * @name CopyButton
 * @description
 * Initializes the state and provides the context for its subcomponents.
 */
interface CopyButtonProps extends React.HTMLAttributes<HTMLSpanElement> {
  value: Value;
  resetDelay?: number;
  children: React.ReactNode;
}
function CopyButton(t0) {
  const $ = _c(16);
  let children;
  let props;
  let resetDelay;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      resetDelay,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = resetDelay;
    $[4] = value;
  } else {
    children = $[1];
    props = $[2];
    resetDelay = $[3];
    value = $[4];
  }
  let t1;
  if ($[5] !== resetDelay) {
    t1 = {
      resetDelay
    };
    $[5] = resetDelay;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const clipboardState = useCopyButton(t1);
  let t2;
  if ($[7] !== clipboardState || $[8] !== value) {
    t2 = {
      ...clipboardState,
      value
    };
    $[7] = clipboardState;
    $[8] = value;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const contextValue = t2;
  let t3;
  if ($[10] !== children || $[11] !== props) {
    t3 = <span className="inline-flex" {...props}>{children}</span>;
    $[10] = children;
    $[11] = props;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== contextValue || $[14] !== t3) {
    t4 = <CopyButtonContext.Provider value={contextValue}>{t3}</CopyButtonContext.Provider>;
    $[13] = contextValue;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}

/**
 * @name CopyTooltip
 * @description Wraps its child with a Tooltip that reacts to the copy state.
 * Accepts all props of TooltipContent from ShadCN/UI.
 */
interface CopyTooltipProps extends Omit<React.ComponentProps<typeof TooltipContent>, 'children'> {
  tooltip?: string;
  copiedTooltip?: string;
  children: React.ReactNode;
  className?: string;
}
function CopyTooltip(t0) {
  const $ = _c(22);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      tooltip: t1,
      copiedTooltip: t2,
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const tooltip = t1 === undefined ? "Copy" : t1;
  const copiedTooltip = t2 === undefined ? "Copied!" : t2;
  const {
    isCopied,
    isHovered,
    setIsHovered
  } = useCopyButtonContext();
  const tooltipText = isCopied ? copiedTooltip : tooltip;
  let t3;
  if ($[6] !== setIsHovered) {
    t3 = () => {
      setIsHovered(true);
    };
    $[6] = setIsHovered;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleMouseEnter = t3;
  let t4;
  if ($[8] !== setIsHovered) {
    t4 = () => {
      setIsHovered(false);
    };
    $[8] = setIsHovered;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleMouseLeave = t4;
  let t5;
  if ($[10] !== children || $[11] !== handleMouseEnter || $[12] !== handleMouseLeave) {
    t5 = <TooltipTrigger asChild={true} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>{children}</TooltipTrigger>;
    $[10] = children;
    $[11] = handleMouseEnter;
    $[12] = handleMouseLeave;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== className || $[15] !== props || $[16] !== tooltipText) {
    t6 = tooltipText && <TooltipContent className={cn("pointer-events-none", className)} sideOffset={4} avoidCollisions={true} collisionPadding={8} {...props}>{tooltipText}</TooltipContent>;
    $[14] = className;
    $[15] = props;
    $[16] = tooltipText;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== isHovered || $[19] !== t5 || $[20] !== t6) {
    t7 = <Tooltip open={isHovered}>{t5}{t6}</Tooltip>;
    $[18] = isHovered;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}

/**
 * @name CopyTrigger
 * @description The button that triggers the copy action.
 * Accepts all props of a Button from ShadCN/UI.
 */
const Trigger = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>((t0, ref) => {
  const $ = _c(13);
  let children;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      onClick,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = onClick;
    $[3] = props;
  } else {
    children = $[1];
    onClick = $[2];
    props = $[3];
  }
  const {
    handleCopy,
    value
  } = useCopyButtonContext();
  let t1;
  if ($[4] !== handleCopy || $[5] !== onClick || $[6] !== value) {
    t1 = e => {
      e.stopPropagation();
      handleCopy(typeof value === "function" ? value() : value);
      onClick?.(e);
    };
    $[4] = handleCopy;
    $[5] = onClick;
    $[6] = value;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const onCopy = t1;
  let t2;
  if ($[8] !== children || $[9] !== onCopy || $[10] !== props || $[11] !== ref) {
    t2 = <Button ref={ref} onClick={onCopy} variant="icon" size="iconXsm" aria-label="Copy to clipboard" {...props}>{children}</Button>;
    $[8] = children;
    $[9] = onCopy;
    $[10] = props;
    $[11] = ref;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
});
Trigger.displayName = 'CopyTrigger';

/**
 * @name CopyAnimatedIcon
 * @description
 * Shows an icon that animates from 'Copy' to a custom icon (defaults to 'Check')
 * when the state changes to copied.
 */
interface AnimatedIconProps extends React.ComponentProps<typeof Copy> {
  copiedIcon?: LucideIcon;
}
const ICON_EASE = [0.32, 0.72, 0, 1] as const;
function AnimatedIcon(t0) {
  const $ = _c(8);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      copiedIcon: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const CopiedIcon = t1 === undefined ? Check : t1;
  const {
    isCopied
  } = useCopyButtonContext();
  const shouldReduceMotion = useReducedMotion();
  let t2;
  if ($[3] !== CopiedIcon || $[4] !== isCopied || $[5] !== props || $[6] !== shouldReduceMotion) {
    t2 = <AnimatePresence mode="wait" initial={false}>{isCopied ? <motion.div key="check" initial={shouldReduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.9
      }} animate={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        scale: [0.9, 1.05, 1]
      }} exit={shouldReduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.9
      }} transition={{
        duration: 0.2,
        ease: ICON_EASE
      }}><CopiedIcon className="size-4" {...props} /></motion.div> : <motion.div key="copy" initial={shouldReduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.9
      }} animate={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        scale: 1
      }} exit={shouldReduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.9
      }} transition={{
        duration: 0.15,
        ease: ICON_EASE
      }}><Copy className="size-4" {...props} /></motion.div>}</AnimatePresence>;
    $[3] = CopiedIcon;
    $[4] = isCopied;
    $[5] = props;
    $[6] = shouldReduceMotion;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { CopyButton, CopyTooltip, Trigger as CopyTrigger, AnimatedIcon as CopyAnimatedIcon };